import type {
  ClientStatus,
  ProjectStatus,
  WebsiteEnvironment,
  WebsiteStatus,
} from "./field-map.js";
import { formatDate } from "./format.js";
import {
  formatClientStatus,
  formatProjectStatus,
  formatWebsiteEnvironment,
  formatWebsiteStatus,
} from "./labels.js";
import { resolveOpenProjectAttention, type ProjectAttention } from "./attention.js";
import type { ProjectAttentionDetail } from "../api/types.js";

/**
 * Operational story for the client Resumo.
 * Only real inventory fields — never ratings, revenue, or invented KPIs.
 */
export interface ClientNarrativeInput {
  readonly clientStatus: ClientStatus;
  readonly websiteReadDenied: boolean;
  readonly primaryDomain: string | null;
  readonly primaryNeedsChoice: boolean;
  readonly primaryWebsiteStatus: WebsiteStatus | null;
  readonly projectStatus: ProjectStatus | null;
  readonly projectNeedsChoice: boolean;
  readonly projectUnavailable: boolean;
  readonly projectReadDenied: boolean;
}

export interface ClientStoryInput {
  readonly clientStatus: ClientStatus;
  readonly websiteReadDenied: boolean;
  readonly primaryDomain: string | null;
  readonly primaryEnvironment: WebsiteEnvironment | null;
  readonly primaryWebsiteStatus: WebsiteStatus | null;
  readonly primaryNeedsChoice: boolean;
  readonly projectName: string | null;
  readonly projectStatus: ProjectStatus | null;
  readonly projectNeedsChoice: boolean;
  readonly projectUnavailable: boolean;
  readonly projectReadDenied: boolean;
  readonly responsible: string | null;
  readonly lastActivityLabel: string;
}

export interface StoryLine {
  /** Visual weight in the narrative block. */
  readonly weight: "status" | "holding" | "meta";
  readonly text: string;
}

/** Real-status signal only — never a scored health index. */
export type SituationSignal = "ok" | "attention" | "neutral" | "risk";

export interface SituationRow {
  readonly id: "client" | "website" | "project";
  readonly label: string;
  readonly signal: SituationSignal;
  /** Decision phrase from real status/env — not inventory. */
  readonly phrase: string;
  /** Quiet secondary line (e.g. last update under project). */
  readonly detail: string | null;
}

export interface ClientSituation {
  readonly rows: readonly SituationRow[];
}

export type NextAttentionKind =
  "choose-website" | "choose-project" | "open-project" | "open-website" | "empty";

/** Decision zone — Situação / Contexto / Ação from real holdings only. */
export interface NextAttention {
  readonly kind: NextAttentionKind;
  /** Attention = forced hop; idle = no forced hop, CTA optional. */
  readonly mode: "attention" | "idle";
  readonly situationSignal: SituationSignal;
  readonly situationPhrase: string;
  readonly context: string;
  readonly actionLabel: string | null;
}

export interface ClientHoldingsInput {
  readonly websiteReadDenied: boolean;
  readonly primaryDomain: string | null;
  readonly primaryEnvironment: WebsiteEnvironment | null;
  readonly primaryWebsiteStatus: WebsiteStatus | null;
  readonly primaryTechnology: string | null;
  readonly primaryNeedsChoice: boolean;
  readonly primarySinceLabel: string | null;
  readonly projectName: string | null;
  readonly projectStatus: ProjectStatus | null;
  readonly projectNeedsChoice: boolean;
  readonly projectUnavailable: boolean;
  readonly projectReadDenied: boolean;
  readonly projectSinceLabel: string | null;
  readonly projectDeadlineLabel: string | null;
}

export interface HoldingLine {
  readonly label: string;
  readonly value: string;
}

export interface ClientHistoryInput {
  readonly clientCreatedAt: string;
  readonly clientCreatedLabel: string;
  readonly websiteCreatedAt: string | null;
  readonly websiteCreatedLabel: string | null;
  readonly websiteDomain: string | null;
  readonly websiteEnvironment: WebsiteEnvironment | null;
  readonly projectCreatedAt: string | null;
  readonly projectCreatedLabel: string | null;
  readonly projectName: string | null;
  readonly lastChangeAt: string;
  readonly lastChangeLabel: string;
}

export interface HistoryEvent {
  readonly id: string;
  readonly at: string;
  readonly text: string;
  readonly whenLabel: string;
}

const projectStatusPhrase = (status: ProjectStatus): string => {
  if (status === "DRAFT") {
    return "projeto em rascunho";
  }
  return `projeto ${formatProjectStatus(status).toLowerCase()}`;
};

const sentenceCase = (value: string): string => {
  if (value.length === 0) {
    return value;
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const withArchived = (archived: boolean, core: string): string => {
  if (!archived) {
    return core;
  }
  return `Cliente arquivado · ${core.charAt(0).toLowerCase()}${core.slice(1)}`;
};

const sameInstant = (left: string, right: string): boolean => {
  const a = new Date(left).getTime();
  const b = new Date(right).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return left === right;
  }
  return a === b;
};

/** One-line operational blurb — kept for compact contexts. */
export const composeClientNarrative = (input: ClientNarrativeInput): string => {
  const archived = input.clientStatus === "ARCHIVED";

  if (input.primaryNeedsChoice) {
    return withArchived(archived, "Vários websites — falta escolher o principal");
  }
  if (input.projectUnavailable) {
    return withArchived(archived, "Projeto ligado indisponível");
  }
  if (input.projectNeedsChoice) {
    return withArchived(archived, "Vários projetos — falta escolher o ativo");
  }
  if (input.websiteReadDenied && input.projectReadDenied) {
    return withArchived(archived, "Sem permissão para ver websites e projetos");
  }

  const parts: string[] = [];
  if (input.primaryDomain !== null) {
    const inactive = input.primaryWebsiteStatus === "INACTIVE" ? " inativo" : "";
    parts.push(`${input.primaryDomain}${inactive}`);
  }

  if (input.projectStatus !== null) {
    const phrase = projectStatusPhrase(input.projectStatus);
    parts.push(parts.length === 0 ? sentenceCase(phrase) : phrase);
  }

  if (parts.length === 0) {
    if (input.websiteReadDenied) {
      return withArchived(archived, "Sem permissão para ver websites");
    }
    if (input.projectReadDenied) {
      return withArchived(archived, "Sem permissão para ver projetos");
    }
    return withArchived(archived, "Ainda sem website nem projeto");
  }

  return withArchived(archived, parts.join(" · "));
};

/** Quiet ownership line — tenure only; no CRM dump. */
export const composeClientHeroMeta = (input: {
  readonly responsible: string | null;
  readonly sinceLabel: string;
}): string => {
  const parts: string[] = [];
  if (input.responsible !== null && input.responsible.length > 0) {
    parts.push(input.responsible);
  }
  parts.push(`Cliente desde ${input.sinceLabel}`);
  return parts.join(" · ");
};

const clientSituationRow = (input: ClientStoryInput): SituationRow => ({
  id: "client",
  label: "Cliente",
  signal: input.clientStatus === "ACTIVE" ? "ok" : "risk",
  phrase: formatClientStatus(input.clientStatus),
  detail: null,
});

/**
 * Website signal from real environment + status only.
 * Prefer "Produção · Ativo" — never invent Online/SSL/uptime/"sem problemas".
 */
const websiteSituationRow = (input: ClientStoryInput): SituationRow => {
  if (input.websiteReadDenied) {
    return {
      id: "website",
      label: "Website",
      signal: "neutral",
      phrase: "Sem permissão",
      detail: null,
    };
  }
  if (input.primaryNeedsChoice) {
    return {
      id: "website",
      label: "Website",
      signal: "attention",
      phrase: "Escolher principal",
      detail: null,
    };
  }
  if (input.primaryDomain === null || input.primaryWebsiteStatus === null) {
    return {
      id: "website",
      label: "Website",
      signal: "neutral",
      phrase: "Ainda sem website",
      detail: null,
    };
  }
  const status = formatWebsiteStatus(input.primaryWebsiteStatus);
  const env =
    input.primaryEnvironment !== null ? formatWebsiteEnvironment(input.primaryEnvironment) : null;
  const phrase = env !== null ? `${env} · ${status}` : status;
  const signal: SituationSignal =
    input.primaryWebsiteStatus === "ACTIVE" && input.primaryEnvironment === "PRODUCTION"
      ? "ok"
      : input.primaryWebsiteStatus === "ACTIVE"
        ? "attention"
        : "risk";
  return {
    id: "website",
    label: "Website",
    signal,
    phrase,
    detail: null,
  };
};

const projectSignal = (status: ProjectStatus): SituationSignal => {
  switch (status) {
    case "COMPLETED":
      return "ok";
    case "IN_PROGRESS":
    case "PLANNED":
    case "DRAFT":
    case "ON_HOLD":
      return "attention";
    case "CANCELLED":
      return "risk";
  }
};

const projectSituationRow = (input: ClientStoryInput): SituationRow => {
  if (input.projectReadDenied) {
    return {
      id: "project",
      label: "Projeto",
      signal: "neutral",
      phrase: "Sem permissão",
      detail: null,
    };
  }
  if (input.projectUnavailable) {
    return {
      id: "project",
      label: "Projeto",
      signal: "risk",
      phrase: "Ligado indisponível",
      detail: null,
    };
  }
  if (input.projectNeedsChoice) {
    return {
      id: "project",
      label: "Projeto",
      signal: "attention",
      phrase: "Escolher ativo",
      detail: null,
    };
  }
  if (input.projectStatus === null) {
    return {
      id: "project",
      label: "Projeto",
      signal: "neutral",
      phrase: "Ainda sem projeto",
      detail: null,
    };
  }
  return {
    id: "project",
    label: "Projeto",
    signal: projectSignal(input.projectStatus),
    phrase: formatProjectStatus(input.projectStatus),
    detail:
      input.lastActivityLabel.length > 0 ? `Última atualização ${input.lastActivityLabel}` : null,
  };
};

/**
 * Cockpit — "Qual é a situação deste cliente?"
 * Signal language from real statuses only; no fake health score / SSL / uptime.
 */
export const composeClientSituation = (input: ClientStoryInput): ClientSituation => ({
  rows: [clientSituationRow(input), websiteSituationRow(input), projectSituationRow(input)],
});

/**
 * Compact story lines — kept for callers that still want prose form.
 * Prefer composeClientSituation for the Resumo cockpit.
 */
export const composeClientStory = (input: ClientStoryInput): readonly StoryLine[] => {
  const situation = composeClientSituation(input);
  const lines: StoryLine[] = situation.rows.map((row, index) => ({
    weight: index === 0 ? "status" : "holding",
    text: `${row.label} · ${row.phrase}`,
  }));

  if (input.responsible !== null && input.responsible.length > 0) {
    lines.push({ weight: "meta", text: `Responsável: ${input.responsible}` });
  }

  const projectDetail = situation.rows.find((row) => row.id === "project")?.detail ?? null;
  if (projectDetail === null) {
    lines.push({
      weight: "meta",
      text: `Última atividade: ${input.lastActivityLabel}`,
    });
  }

  return lines;
};

const joinDecisionParts = (parts: readonly (string | null)[]): string =>
  parts.filter((part): part is string => part !== null && part.length > 0).join(" · ");

const projectAttentionContext = (input: {
  readonly projectDescription: string | null;
  readonly projectName: string | null;
  readonly responsible: string | null;
  readonly projectUpdatedRelative: string | null;
}): string => {
  const lead =
    input.projectDescription !== null && input.projectDescription.length > 0
      ? input.projectDescription
      : input.projectName;
  const update =
    input.projectUpdatedRelative !== null && input.projectUpdatedRelative.length > 0
      ? `última atualização ${input.projectUpdatedRelative}`
      : null;
  return joinDecisionParts([lead, input.responsible, update]);
};

/** Honest idle line — website env/status + project status when known. */
const idleDecisionContext = (input: {
  readonly primaryDomain: string | null;
  readonly primaryEnvironment: WebsiteEnvironment | null;
  readonly primaryWebsiteStatus: WebsiteStatus | null;
  readonly projectStatus: ProjectStatus | null;
}): string => {
  const websitePart = ((): string | null => {
    if (input.primaryDomain === null) {
      return null;
    }
    if (input.primaryEnvironment === "PRODUCTION" && input.primaryWebsiteStatus === "ACTIVE") {
      return "Website em produção";
    }
    if (input.primaryEnvironment !== null) {
      return `Website ${formatWebsiteEnvironment(input.primaryEnvironment).toLowerCase()}`;
    }
    if (input.primaryWebsiteStatus !== null) {
      return `Website ${formatWebsiteStatus(input.primaryWebsiteStatus).toLowerCase()}`;
    }
    return "Website conhecido";
  })();
  const projectPart =
    input.projectStatus !== null
      ? `Projeto ${formatProjectStatus(input.projectStatus).toLowerCase()}`
      : null;
  const line = joinDecisionParts([websitePart, projectPart]);
  return line.length > 0 ? line : "Ainda sem website nem projeto";
};

/**
 * Decision block from real holdings only — never invents pending tasks/deadlines.
 * Answers WHY the CTA exists (Situação + Contexto) before the hop (Ação).
 */
export const composeNextAttention = (input: {
  readonly primaryNeedsChoice: boolean;
  readonly projectNeedsChoice: boolean;
  readonly projectUnavailable: boolean;
  readonly hasOpenableProject: boolean;
  readonly hasOpenableWebsite: boolean;
  readonly primaryDomain: string | null;
  readonly primaryEnvironment: WebsiteEnvironment | null;
  readonly primaryWebsiteStatus: WebsiteStatus | null;
  readonly projectStatus: ProjectStatus | null;
  readonly projectDescription: string | null;
  readonly projectName: string | null;
  readonly responsible: string | null;
  readonly projectUpdatedRelative: string | null;
}): NextAttention => {
  const idleContext = idleDecisionContext(input);

  if (input.primaryNeedsChoice) {
    return {
      kind: "choose-website",
      mode: "attention",
      situationSignal: "attention",
      situationPhrase: "Escolher website principal",
      context: "Vários websites — falta escolher o principal",
      actionLabel: null,
    };
  }
  if (input.projectNeedsChoice) {
    return {
      kind: "choose-project",
      mode: "attention",
      situationSignal: "attention",
      situationPhrase: "Escolher projeto ativo",
      context: "Vários projetos — falta escolher o ativo",
      actionLabel: null,
    };
  }
  if (input.projectUnavailable) {
    if (input.hasOpenableWebsite) {
      return {
        kind: "open-website",
        mode: "attention",
        situationSignal: "risk",
        situationPhrase: "Projeto ligado indisponível",
        context: idleContext,
        actionLabel: "Abrir Website",
      };
    }
    return {
      kind: "empty",
      mode: "attention",
      situationSignal: "risk",
      situationPhrase: "Projeto ligado indisponível",
      context: idleContext,
      actionLabel: null,
    };
  }

  if (input.hasOpenableProject && input.projectStatus !== null) {
    const signal = projectSignal(input.projectStatus);
    if (signal === "attention" || signal === "risk") {
      return {
        kind: "open-project",
        mode: "attention",
        situationSignal: signal,
        situationPhrase: `Projeto ${formatProjectStatus(input.projectStatus).toLowerCase()}`,
        context: projectAttentionContext(input),
        actionLabel: "Abrir Projeto",
      };
    }
    return {
      kind: "open-project",
      mode: "idle",
      situationSignal: "ok",
      situationPhrase: `Projeto ${formatProjectStatus(input.projectStatus).toLowerCase()}`,
      context: idleContext,
      actionLabel: "Abrir Projeto",
    };
  }

  if (input.hasOpenableWebsite) {
    const websiteOk =
      input.primaryWebsiteStatus === "ACTIVE" && input.primaryEnvironment === "PRODUCTION";
    if (websiteOk) {
      return {
        kind: "open-website",
        mode: "idle",
        situationSignal: "ok",
        situationPhrase: `${formatWebsiteEnvironment(input.primaryEnvironment)} · ${formatWebsiteStatus(input.primaryWebsiteStatus)}`,
        actionLabel: "Abrir Website",
        context: idleContext,
      };
    }
    const phrase =
      input.primaryEnvironment !== null
        ? `Website ${formatWebsiteEnvironment(input.primaryEnvironment).toLowerCase()}`
        : input.primaryWebsiteStatus !== null
          ? `Website ${formatWebsiteStatus(input.primaryWebsiteStatus).toLowerCase()}`
          : "Abrir website";
    return {
      kind: "open-website",
      mode: "attention",
      situationSignal: "attention",
      situationPhrase: phrase,
      context: idleContext,
      actionLabel: "Abrir Website",
    };
  }

  return {
    kind: "empty",
    mode: "idle",
    situationSignal: "neutral",
    situationPhrase: "Ainda sem website nem projeto",
    context: idleContext,
    actionLabel: null,
  };
};

const websiteHoldingValue = (input: ClientHoldingsInput): string => {
  if (input.websiteReadDenied) {
    return "Sem permissão";
  }
  if (input.primaryNeedsChoice) {
    return "Vários — falta escolher o principal";
  }
  if (input.primaryDomain === null) {
    return "Ainda sem website";
  }
  const parts = [input.primaryDomain];
  if (input.primaryEnvironment !== null) {
    parts.push(formatWebsiteEnvironment(input.primaryEnvironment));
  }
  if (input.primaryWebsiteStatus !== null) {
    parts.push(formatWebsiteStatus(input.primaryWebsiteStatus));
  }
  if (input.primaryTechnology !== null && input.primaryTechnology.length > 0) {
    parts.push(input.primaryTechnology);
  }
  if (input.primarySinceLabel !== null) {
    parts.push(`desde ${input.primarySinceLabel}`);
  }
  return parts.join(" · ");
};

const projectHoldingValue = (input: ClientHoldingsInput): string => {
  if (input.projectReadDenied) {
    return "Sem permissão";
  }
  if (input.projectUnavailable) {
    return "Projeto ligado indisponível";
  }
  if (input.projectNeedsChoice) {
    return "Vários — falta escolher o ativo";
  }
  if (input.projectStatus === null) {
    return "Ainda sem projeto";
  }
  const parts: string[] = [];
  if (input.projectName !== null && input.projectName.length > 0) {
    parts.push(input.projectName);
  }
  parts.push(formatProjectStatus(input.projectStatus));
  if (input.projectSinceLabel !== null) {
    parts.push(`desde ${input.projectSinceLabel}`);
  }
  if (input.projectDeadlineLabel !== null) {
    parts.push(`prazo ${input.projectDeadlineLabel}`);
  }
  return parts.join(" · ");
};

/**
 * Holdings — "O que temos com este cliente?"
 * Quieter inventory: domain, environment, technology, dates. Not a fiche dump.
 */
export const composeClientHoldings = (input: ClientHoldingsInput): readonly HoldingLine[] => [
  { label: "Website", value: websiteHoldingValue(input) },
  { label: "Projeto", value: projectHoldingValue(input) },
];

const websiteHistoryText = (domain: string, environment: WebsiteEnvironment | null): string => {
  if (environment === "PRODUCTION") {
    return `Website em Produção · ${domain}`;
  }
  if (environment === "STAGING") {
    return `Website em Staging · ${domain}`;
  }
  return `Website criado · ${domain}`;
};

/**
 * Compact histórico — important real events only. No Deploy placeholder.
 */
export const composeClientHistory = (input: ClientHistoryInput): readonly HistoryEvent[] => {
  const events: HistoryEvent[] = [
    {
      id: "client-created",
      at: input.clientCreatedAt,
      text: "Cliente criado",
      whenLabel: input.clientCreatedLabel,
    },
  ];

  if (
    input.websiteCreatedAt !== null &&
    input.websiteCreatedLabel !== null &&
    input.websiteDomain !== null
  ) {
    events.push({
      id: "website-created",
      at: input.websiteCreatedAt,
      text: websiteHistoryText(input.websiteDomain, input.websiteEnvironment),
      whenLabel: input.websiteCreatedLabel,
    });
  }

  if (input.projectCreatedAt !== null && input.projectCreatedLabel !== null) {
    const name =
      input.projectName !== null && input.projectName.length > 0 ? ` · ${input.projectName}` : "";
    events.push({
      id: "project-started",
      at: input.projectCreatedAt,
      text: `Projeto iniciado${name}`,
      whenLabel: input.projectCreatedLabel,
    });
  }

  const covered = events.some((event) => sameInstant(event.at, input.lastChangeAt));
  if (!covered) {
    events.push({
      id: "last-change",
      at: input.lastChangeAt,
      text: "Última alteração",
      whenLabel: input.lastChangeLabel,
    });
  }

  return events.sort((left, right) => {
    const a = new Date(left.at).getTime();
    const b = new Date(right.at).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) {
      return 0;
    }
    return b - a;
  });
};

/** Website Workspace cockpit — situation from real env + status only. */
export interface WebsiteWorkspaceInput {
  readonly domain: string;
  readonly environment: WebsiteEnvironment;
  readonly status: WebsiteStatus;
  readonly technology: string | null;
  readonly responsible: string | null;
  /** Relative label from updatedAt ("Há 2 dias") — caller formats. */
  readonly updatedRelative: string;
  readonly projectName: string | null;
  readonly projectStatus: ProjectStatus | null;
  readonly projectNeedsChoice: boolean;
  readonly projectUnavailable: boolean;
  readonly hasOpenableProject: boolean;
  readonly websiteCreatedAt: string;
  readonly websiteCreatedLabel: string;
  readonly projectUpdatedAt: string | null;
  readonly projectUpdatedLabel: string | null;
}

export interface WebsiteProjectLine {
  readonly signal: SituationSignal;
  readonly phrase: string;
}

export type WebsiteActionKind = "open-project" | "back-to-client" | "choose-project";

export interface WebsiteWorkspaceView {
  readonly situationSignal: SituationSignal;
  readonly situationPhrase: string;
  readonly context: string;
  readonly project: WebsiteProjectLine;
  readonly activity: readonly HistoryEvent[];
  readonly actionKind: WebsiteActionKind;
  readonly actionLabel: string;
}

const websiteSituationSignal = (
  environment: WebsiteEnvironment,
  status: WebsiteStatus,
): SituationSignal => {
  if (status === "ACTIVE" && environment === "PRODUCTION") {
    return "ok";
  }
  if (status === "ACTIVE") {
    return "attention";
  }
  return "risk";
};

const websiteSituationPhrase = (environment: WebsiteEnvironment, status: WebsiteStatus): string =>
  `${formatWebsiteEnvironment(environment)} · ${formatWebsiteStatus(status)}`;

const websiteContextLine = (input: WebsiteWorkspaceInput): string => {
  const update =
    input.updatedRelative.length > 0
      ? `atualizado ${input.updatedRelative.charAt(0).toLowerCase()}${input.updatedRelative.slice(1)}`
      : null;
  return joinDecisionParts([
    input.domain,
    formatWebsiteEnvironment(input.environment),
    input.technology,
    input.responsible,
    update,
  ]);
};

const websiteProjectLine = (input: WebsiteWorkspaceInput): WebsiteProjectLine => {
  if (input.projectUnavailable) {
    return { signal: "risk", phrase: "Ligado indisponível" };
  }
  if (input.projectNeedsChoice) {
    return { signal: "attention", phrase: "Escolher ativo" };
  }
  if (input.projectStatus === null) {
    return { signal: "neutral", phrase: "Sem projeto ligado" };
  }
  const status = formatProjectStatus(input.projectStatus);
  const phrase =
    input.projectName !== null && input.projectName.length > 0
      ? `${input.projectName} · ${status}`
      : status;
  return { signal: projectSignal(input.projectStatus), phrase };
};

/**
 * Compact website activity — real timestamps only. No Deploy placeholder.
 */
export const composeWebsiteActivity = (input: {
  readonly websiteCreatedAt: string;
  readonly websiteCreatedLabel: string;
  readonly domain: string;
  readonly environment: WebsiteEnvironment;
  readonly projectUpdatedAt: string | null;
  readonly projectUpdatedLabel: string | null;
  readonly projectName: string | null;
}): readonly HistoryEvent[] => {
  const events: HistoryEvent[] = [
    {
      id: "website-created",
      at: input.websiteCreatedAt,
      text: websiteHistoryText(input.domain, input.environment),
      whenLabel: input.websiteCreatedLabel,
    },
  ];

  if (
    input.projectUpdatedAt !== null &&
    input.projectUpdatedLabel !== null &&
    !sameInstant(input.projectUpdatedAt, input.websiteCreatedAt)
  ) {
    const name =
      input.projectName !== null && input.projectName.length > 0 ? ` · ${input.projectName}` : "";
    events.push({
      id: "project-updated",
      at: input.projectUpdatedAt,
      text: `Projeto atualizado${name}`,
      whenLabel: input.projectUpdatedLabel,
    });
  }

  return events.sort((left, right) => {
    const a = new Date(left.at).getTime();
    const b = new Date(right.at).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) {
      return 0;
    }
    return b - a;
  });
};

/**
 * Website Workspace cockpit — "Está ok? O que acontece? Onde continuar?"
 * Real env/status/project only — never SSL, uptime, Deploy, Analytics.
 */
export const composeWebsiteWorkspace = (input: WebsiteWorkspaceInput): WebsiteWorkspaceView => {
  const project = websiteProjectLine(input);
  const action = ((): { readonly kind: WebsiteActionKind; readonly label: string } => {
    if (input.projectNeedsChoice) {
      return { kind: "choose-project", label: "Escolher projeto abaixo" };
    }
    if (input.hasOpenableProject) {
      return { kind: "open-project", label: "Abrir Projeto" };
    }
    return { kind: "back-to-client", label: "Voltar ao cliente" };
  })();

  return {
    situationSignal: websiteSituationSignal(input.environment, input.status),
    situationPhrase: websiteSituationPhrase(input.environment, input.status),
    context: websiteContextLine(input),
    project,
    activity: composeWebsiteActivity({
      websiteCreatedAt: input.websiteCreatedAt,
      websiteCreatedLabel: input.websiteCreatedLabel,
      domain: input.domain,
      environment: input.environment,
      projectUpdatedAt: input.projectUpdatedAt,
      projectUpdatedLabel: input.projectUpdatedLabel,
      projectName: input.projectName,
    }),
    actionKind: action.kind,
    actionLabel: action.label,
  };
};

/** Project Workspace cockpit — status → context → linked website → activity → hop. */
export interface ProjectWorkspaceInput {
  readonly status: ProjectStatus;
  readonly name: string;
  readonly description: string | null;
  readonly responsible: string | null;
  /** Relative label from updatedAt ("Há 2 dias") — caller formats. */
  readonly updatedRelative: string;
  readonly websiteDomain: string | null;
  readonly websiteEnvironment: WebsiteEnvironment | null;
  readonly websiteStatus: WebsiteStatus | null;
  readonly hasOpenableWebsite: boolean;
  readonly projectCreatedAt: string;
  readonly projectCreatedLabel: string;
  readonly projectUpdatedAt: string;
  readonly projectUpdatedLabel: string;
  readonly openAttentions: readonly ProjectAttentionDetail[];
}

export interface ProjectWebsiteLine {
  readonly signal: SituationSignal;
  readonly phrase: string;
}

/** Client-communicated operational signal — not UTURA OS-detected. */
export type { ProjectAttention };

export interface ProjectContextField {
  readonly label: string;
  readonly value: string;
}

export type ProjectActionKind = "open-website" | "back-to-client" | "view-attention";

export interface ProjectWorkspaceView {
  readonly situationSignal: SituationSignal;
  readonly situationPhrase: string;
  readonly context: string;
  readonly contextFields: readonly ProjectContextField[];
  readonly attention: ProjectAttention | null;
  readonly website: ProjectWebsiteLine | null;
  readonly activity: readonly HistoryEvent[];
  readonly actionKind: ProjectActionKind;
  readonly actionLabel: string;
}

const projectContextLine = (input: ProjectWorkspaceInput): string => {
  const update =
    input.updatedRelative.length > 0
      ? `atualizado ${input.updatedRelative.charAt(0).toLowerCase()}${input.updatedRelative.slice(1)}`
      : null;
  const description =
    input.description !== null && input.description.trim().length > 0
      ? input.description.trim()
      : null;
  return joinDecisionParts([input.name, description, input.responsible, update]);
};

const projectContextFields = (input: ProjectWorkspaceInput): readonly ProjectContextField[] => {
  const fields: ProjectContextField[] = [];
  if (input.responsible !== null && input.responsible.length > 0) {
    fields.push({ label: "Responsável", value: input.responsible });
  }
  if (input.description !== null && input.description.trim().length > 0) {
    fields.push({ label: "Descrição", value: input.description.trim() });
  }
  fields.push({ label: "Início", value: formatDate(input.projectCreatedAt) });
  fields.push({ label: "Última atualização", value: input.projectUpdatedLabel });
  return fields;
};

const projectWebsiteLine = (input: ProjectWorkspaceInput): ProjectWebsiteLine | null => {
  if (
    input.websiteDomain === null ||
    input.websiteEnvironment === null ||
    input.websiteStatus === null
  ) {
    return null;
  }
  return {
    signal: websiteSituationSignal(input.websiteEnvironment, input.websiteStatus),
    phrase: joinDecisionParts([
      input.websiteDomain,
      formatWebsiteEnvironment(input.websiteEnvironment),
      formatWebsiteStatus(input.websiteStatus),
    ]),
  };
};

/**
 * Compact project activity — real timestamps only. No Deploy placeholder.
 */
export const composeProjectActivity = (input: {
  readonly projectCreatedAt: string;
  readonly projectCreatedLabel: string;
  readonly projectUpdatedAt: string;
  readonly projectUpdatedLabel: string;
  readonly projectName: string;
}): readonly HistoryEvent[] => {
  const events: HistoryEvent[] = [
    {
      id: "project-created",
      at: input.projectCreatedAt,
      text: `Projeto criado · ${input.projectName}`,
      whenLabel: input.projectCreatedLabel,
    },
  ];

  if (!sameInstant(input.projectUpdatedAt, input.projectCreatedAt)) {
    events.push({
      id: "project-updated",
      at: input.projectUpdatedAt,
      text: `Projeto atualizado · ${input.projectName}`,
      whenLabel: input.projectUpdatedLabel,
    });
  }

  return events.sort((left, right) => {
    const a = new Date(left.at).getTime();
    const b = new Date(right.at).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) {
      return 0;
    }
    return b - a;
  });
};

/**
 * Project Workspace cockpit — Situação → Atenção? → Contexto → Atividade → Ação.
 * Real status / description / linked website only — never Deploy, CRM, or auto-detected billing.
 */
export const composeProjectWorkspace = (input: ProjectWorkspaceInput): ProjectWorkspaceView => {
  const attention = resolveOpenProjectAttention(input.openAttentions);
  const website = attention === null ? projectWebsiteLine(input) : null;
  const action = ((): { readonly kind: ProjectActionKind; readonly label: string } => {
    if (attention !== null) {
      return { kind: "view-attention", label: "Ver atenção" };
    }
    if (input.hasOpenableWebsite) {
      return { kind: "open-website", label: "Abrir Website" };
    }
    return { kind: "back-to-client", label: "Voltar ao cliente" };
  })();

  return {
    situationSignal: projectSignal(input.status),
    situationPhrase: formatProjectStatus(input.status),
    context: projectContextLine(input),
    contextFields: projectContextFields(input),
    attention,
    website,
    activity: composeProjectActivity({
      projectCreatedAt: input.projectCreatedAt,
      projectCreatedLabel: input.projectCreatedLabel,
      projectUpdatedAt: input.projectUpdatedAt,
      projectUpdatedLabel: input.projectUpdatedLabel,
      projectName: input.name,
    }),
    actionKind: action.kind,
    actionLabel: action.label,
  };
};
